import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useRef, useState } from 'react';
import { TranslationProvider, useTranslation } from '@ibk/shared-translation';
import { DiscountType, ReleaseType } from '@ibk/shared-types';
import { Alert, Button, Checkbox, Icon, Loading, Tooltip } from '@ibk/shared-ui';
import { beneficiosTranslations } from '../../translations';
import { BeneficiaryStatusInProduct } from '../../types';
import { getUnformatedValue, hasAnyFilter, isActiveStatus } from '../../utils/beneficiaryUtils';
import { BeneficiaryFilter } from '../BeneficiaryFilter';
import { BeneficiaryListItem } from '../BeneficiaryListItem';
import styles from './BeneficiaryValuesForm.module.css';
function BeneficiaryValuesFormContent({ context, releaseType, globalValue, beneficiaries, businessDays, showAlert = false, isLoading = false, appliedFilters = null, isBeneficiaryDiscountEnabled = false, payrollDeduction = false, discountType = DiscountType.TOTAL, discountValue, onReleaseTypeChange, onGlobalValueChange, onUpdateBeneficiaries, onCloseAlert, onApplyValue, onBusinessDaysChange, onFilterChange, readonly = false, getRechargeHistory, isFiltering = false, hasZeroValueError = false, filterOptions, isLoadingFilters, showAnalysisStatus = false, infoAsPlainText = false, hideTypeColumn = false, beneficiariesLabel, valueColumnLabel, showActionColumn = false, onCorrectBeneficiary, filterActions, filterVariant, }) {
    const { t } = useTranslation();
    const [globalBusinessDays, setGlobalBusinessDays] = useState(0);
    const filterRef = useRef(null);
    const isRechargeContext = context === 'recharge';
    const showBusinessDaysInput = isRechargeContext && releaseType === ReleaseType.DAILY;
    React.useEffect(() => {
        if (businessDays !== null && businessDays !== undefined) {
            setGlobalBusinessDays(businessDays);
        }
    }, [businessDays]);
    if (isLoading) {
        return (_jsx("div", { className: styles.loadingContainer, "data-testid": 'viewBeneficiaryValuesFormLoading', children: _jsx(Loading, {}) }));
    }
    const hasActiveFilters = appliedFilters ? hasAnyFilter(appliedFilters, true) : false;
    const showEmptyState = hasActiveFilters && beneficiaries.length === 0;
    const allSelected = beneficiaries.length > 0 && beneficiaries.every(beneficiary => isActiveStatus(beneficiary.statusInProduct));
    const someSelected = beneficiaries.some(beneficiary => isActiveStatus(beneficiary.statusInProduct));
    const isIndeterminate = someSelected && !allSelected;
    const getAlertMessage = (releaseType) => {
        if (isRechargeContext) {
            return releaseType === ReleaseType.MONTHLY
                ? t('recharge.step.beneficiaries-values.info.monthly')
                : t('recharge.step.beneficiaries-values.info.daily');
        }
        return releaseType === ReleaseType.MONTHLY
            ? t('parameters.step2.alert.monthly')
            : t('parameters.step2.alert.daily');
    };
    const handleSelectAllBeneficiaries = () => {
        const newStatus = allSelected ? BeneficiaryStatusInProduct.INACTIVE : BeneficiaryStatusInProduct.ACTIVE;
        const updatedBeneficiaries = beneficiaries.map(beneficiary => {
            if (beneficiary.statusInProduct === newStatus) {
                return beneficiary;
            }
            return updateBeneficiaryDataOnSelectChange(beneficiary, newStatus);
        });
        onUpdateBeneficiaries(updatedBeneficiaries);
    };
    const handleSelectBeneficiary = (id) => {
        const updatedBeneficiaries = beneficiaries.map(beneficiary => {
            if (beneficiary.id === id) {
                const isCurrentlyActive = isActiveStatus(beneficiary.statusInProduct);
                const newStatus = isCurrentlyActive ? BeneficiaryStatusInProduct.INACTIVE : BeneficiaryStatusInProduct.ACTIVE;
                return updateBeneficiaryDataOnSelectChange(beneficiary, newStatus);
            }
            return beneficiary;
        });
        onUpdateBeneficiaries(updatedBeneficiaries);
    };
    const updateBeneficiaryDataOnSelectChange = (beneficiary, newStatus) => {
        let productValue = 0;
        let productDaysQuantity;
        let productDiscountValue = 0;
        if (newStatus === BeneficiaryStatusInProduct.ACTIVE) {
            const isDaily = releaseType === ReleaseType.DAILY;
            if (!isDaily) {
                productValue = getUnformatedValue(globalValue) || 0;
            }
            else if (isRechargeContext) {
                productValue = getUnformatedValue(globalValue) * globalBusinessDays;
                productDaysQuantity = globalBusinessDays;
            }
            if (isBeneficiaryDiscountEnabled && payrollDeduction && discountValue) {
                productDiscountValue = getUnformatedValue(discountValue) || 0;
            }
        }
        return {
            ...beneficiary,
            statusInProduct: newStatus,
            productValue,
            productDaysQuantity,
            productDiscountValue,
        };
    };
    const clearImportedField = (beneficiary, field) => {
        if (!beneficiary.importedFields) {
            return beneficiary.importedFields;
        }
        const rest = Object.fromEntries(Object.entries(beneficiary.importedFields).filter(([key]) => key !== field));
        return rest && Object.keys(rest).length > 0 ? rest : undefined;
    };
    const handleUpdateBeneficiary = (id, value) => {
        const updatedBeneficiaries = beneficiaries.map(beneficiary => {
            if (beneficiary.id === id) {
                return {
                    ...beneficiary,
                    productValue: getUnformatedValue(value),
                    importedFields: clearImportedField(beneficiary, 'value'),
                };
            }
            return beneficiary;
        });
        onUpdateBeneficiaries(updatedBeneficiaries);
    };
    const handleUpdateBeneficiaryDiscount = (id, value) => {
        const updatedBeneficiaries = beneficiaries.map(beneficiary => {
            if (beneficiary.id === id) {
                return {
                    ...beneficiary,
                    productDiscountValue: getUnformatedValue(value),
                    importedFields: clearImportedField(beneficiary, 'discount'),
                };
            }
            return beneficiary;
        });
        onUpdateBeneficiaries(updatedBeneficiaries);
    };
    const handleBusinessDaysChange = (value) => {
        const days = value === '' ? 0 : parseInt(value) || 0;
        setGlobalBusinessDays(days);
        onBusinessDaysChange?.(days);
    };
    const handleGlobalValueChange = (value) => {
        onGlobalValueChange?.(value);
        if (isRechargeContext && releaseType === ReleaseType.DAILY) {
            const updatedBeneficiaries = beneficiaries.map(beneficiary => {
                if (isActiveStatus(beneficiary.statusInProduct)) {
                    const productDaysQuantity = beneficiary.productDaysQuantity ?? globalBusinessDays;
                    return {
                        ...beneficiary,
                        productValue: getUnformatedValue(value) * productDaysQuantity,
                        importedFields: undefined,
                    };
                }
                return beneficiary;
            });
            onUpdateBeneficiaries(updatedBeneficiaries);
        }
    };
    const handleApplyBusinessDays = () => {
        const globalNumericValue = getUnformatedValue(globalValue);
        const updatedBeneficiaries = beneficiaries.map(beneficiary => {
            if (isActiveStatus(beneficiary.statusInProduct)) {
                if (releaseType === ReleaseType.DAILY) {
                    const newProductValue = globalNumericValue * globalBusinessDays;
                    return {
                        ...beneficiary,
                        productDaysQuantity: globalBusinessDays,
                        productValue: newProductValue,
                        importedFields: undefined,
                    };
                }
                return {
                    ...beneficiary,
                    productDaysQuantity: globalBusinessDays,
                    productValue: globalNumericValue,
                    importedFields: undefined,
                };
            }
            return beneficiary;
        });
        onUpdateBeneficiaries(updatedBeneficiaries);
    };
    const handleUpdateBeneficiaryBusinessDays = (id, days) => {
        const globalNumericValue = getUnformatedValue(globalValue);
        const updatedBeneficiaries = beneficiaries.map(beneficiary => {
            if (beneficiary.id === id) {
                if (releaseType === ReleaseType.DAILY) {
                    const newProductValue = globalNumericValue * days;
                    return {
                        ...beneficiary,
                        productDaysQuantity: days,
                        productValue: newProductValue,
                        importedFields: clearImportedField(beneficiary, 'days'),
                    };
                }
                return {
                    ...beneficiary,
                    productDaysQuantity: days,
                    importedFields: clearImportedField(beneficiary, 'days'),
                };
            }
            return beneficiary;
        });
        onUpdateBeneficiaries(updatedBeneficiaries);
    };
    const renderLoading = () => {
        return (_jsx("div", { className: styles.loadingContainer, "data-testid": 'viewBeneficiaryValuesFormLoading', children: _jsx("span", { className: styles.loadingIcon, children: _jsx(Loading, { size: 'micro' }) }) }));
    };
    const renderNoResults = () => {
        return (_jsxs("div", { className: styles.noResultsContainer, "data-testid": 'viewBeneficiaryValuesFormNoResultsContainer', children: [_jsx(Icon, { name: 'circleError' }), _jsxs("div", { className: styles.noResultsContent, children: [_jsx("h3", { className: styles.noResultsTitle, "data-testid": 'viewBeneficiaryValuesFormNoResultsTitle', children: t('parameters.step2.no-results.title') }), _jsx("p", { className: styles.noResultsDescription, "data-testid": 'viewBeneficiaryValuesFormNoResultsDescription', children: t('parameters.step2.no-results.description') })] }), _jsx(Button, { size: 'medium', variant: 'secondary', onClick: () => filterRef.current?.clearFilters(), dataTestId: 'buttonBeneficiaryValuesFormClearFilters', children: t('clearFilters') })] }));
    };
    const renderBeneficiariesContent = () => {
        if (isFiltering) {
            return renderLoading();
        }
        if (showEmptyState) {
            return renderNoResults();
        }
        return (_jsxs("div", { className: styles.beneficiariesContent, children: [_jsxs("div", { className: styles.beneficiariesHeader, "data-testid": 'viewBeneficiaryValuesFormBeneficiariesHeader', children: [_jsx(Checkbox, { checked: allSelected, indeterminate: isIndeterminate, onChange: handleSelectAllBeneficiaries, "data-testid": 'checkboxBeneficiaryValuesFormSelectAll', disabled: readonly }), _jsxs("div", { className: styles.beneficiariesHeaderContent, "data-testid": 'viewBeneficiaryValuesFormBeneficiariesHeaderContent', children: [_jsx("span", { className: styles.beneficiariesHeaderLabel, "data-testid": 'textBeneficiaryValuesFormHeaderBeneficiaries', children: beneficiariesLabel ?? t('parameters.step2.beneficiaries.header.beneficiaries') }), !hideTypeColumn && (_jsx("span", { className: styles.beneficiariesHeaderType, "data-testid": 'textBeneficiaryValuesFormHeaderType', children: t('parameters.step2.beneficiaries.header.type') })), _jsx("span", { className: styles.beneficiariesHeaderCard, "data-testid": 'textBeneficiaryValuesFormHeaderCard', children: t('parameters.step2.beneficiaries.header.card') }), isRechargeContext && releaseType === ReleaseType.DAILY && (_jsx("span", { className: styles.beneficiariesHeaderDays, "data-testid": 'textBeneficiaryValuesFormHeaderDays', children: t('days') })), (isRechargeContext || releaseType === ReleaseType.MONTHLY) && (_jsx("span", { className: styles.beneficiariesHeaderTotal, "data-testid": 'textBeneficiaryValuesFormHeaderTotal', children: valueColumnLabel ?? t('recharge.beneficiaries.column.value') })), isBeneficiaryDiscountEnabled && (_jsxs("span", { className: styles.beneficiariesHeaderDiscount, "data-testid": 'textBeneficiaryValuesFormHeaderDiscount', children: [t('parameters.step2.beneficiaries.header.discount'), _jsx(Tooltip, { content: t('parameters.step2.beneficiaries.header.discount.tooltip'), children: _jsx(Icon, { name: 'actionInformationDark', width: 12, height: 12 }) })] })), showAnalysisStatus && (_jsx("span", { className: styles.beneficiariesHeaderStatus, "data-testid": 'textBeneficiaryValuesFormHeaderStatus', children: t('recharge.beneficiaries.analysis-status.header') })), showActionColumn && (_jsx("span", { className: styles.beneficiariesHeaderActions, "data-testid": 'textBeneficiaryValuesFormHeaderActions', children: t('recharge.beneficiaries.actions.header') }))] })] }), _jsx("div", { className: styles.beneficiariesList, "data-testid": 'viewBeneficiaryValuesFormBeneficiariesList', children: beneficiaries.map((beneficiary, index) => (_jsx(BeneficiaryListItem, { releaseType: releaseType, beneficiary: beneficiary, onSelect: handleSelectBeneficiary, onUpdate: handleUpdateBeneficiary, onUpdateBusinessDays: handleUpdateBeneficiaryBusinessDays, onUpdateDiscount: handleUpdateBeneficiaryDiscount, isRechargeContext: isRechargeContext, isBeneficiaryDiscountEnabled: isBeneficiaryDiscountEnabled, payrollDeduction: payrollDeduction, discountType: discountType, testIdIdentifier: `BeneficiaryValuesFormBeneficiaryListItem${index}`, hasActiveRechargeHistory: getRechargeHistory?.(beneficiary.id) ?? false, hasZeroValueError: hasZeroValueError, showAnalysisStatus: showAnalysisStatus, hideTypeColumn: hideTypeColumn, showActionColumn: showActionColumn, onCorrect: onCorrectBeneficiary }, beneficiary.id))) })] }));
    };
    return (_jsxs(_Fragment, { children: [infoAsPlainText ? (_jsx("p", { className: styles.infoText, "data-testid": 'textBeneficiaryValuesFormInfo', children: getAlertMessage(releaseType) })) : (showAlert &&
                onCloseAlert && (_jsx(Alert, { alertType: 'inline', appearance: 'info', message: getAlertMessage(releaseType), onClose: onCloseAlert, "data-testid": 'alertBeneficiaryValuesFormInfo', alignItems: 'center' }))), _jsxs("div", { className: styles.formSection, "data-testid": 'viewBeneficiaryValuesFormBeneficiariesSection', children: [_jsx(BeneficiaryFilter, { ref: filterRef, onFilter: filters => onFilterChange?.(filters), testIdIdentifier: 'BeneficiaryValuesFormBeneficiaryFilter', initialFilters: appliedFilters, filterOptions: filterOptions, isLoadingFilters: isLoadingFilters, trailingActions: filterActions, filterVariant: filterVariant }), renderBeneficiariesContent()] })] }));
}
export function BeneficiaryValuesForm(props) {
    return (_jsx(TranslationProvider, { translations: beneficiosTranslations, children: _jsx(BeneficiaryValuesFormContent, { ...props }) }));
}
